import { Link } from 'wouter';
import { CtaBand, Hero, Section } from '../components/ui/primitives';
import { ProductGrid } from '../components/ui/ProductGrid';
import { usePageMeta } from '../lib/seo';

/**
 * Home. Baseline §3 — six sections, in this order, with these backgrounds:
 *   1 navy hero (593px)  2 white trust strip (32px 0)  3 muted product range
 *   4 white "Why Choose"  5 muted process  6 navy CTA band (96px 16px)
 *
 * All copy is production copy, preserved verbatim (P-13).
 */

const WHY_CHOOSE = [
  {
    title: 'Compliant Partner',
    body: 'Fully registered on CSD with tax clearance and B-BBEE documentation ready for your procurement file.',
  },
  {
    title: 'Local Service Promise',
    body: 'Based in Phalaborwa. We know the schools, the routes and the term calendar.',
  },
  {
    title: 'No Retail Markup',
    body: 'Institutional pricing on bulk quantities. Transparent quotes with no hidden fees.',
  },
  {
    title: 'Direct Contact',
    body: 'Speak to a person, not a call centre. Quotes prepared and followed up personally.',
  },
] as const;

const PROCESS = [
  { n: '1', title: 'Send your list', body: 'Tell us what you need — by form, email or WhatsApp.' },
  { n: '2', title: 'Receive a formal quote', body: 'We prepare and email a formal quotation within 24 hours.' },
  { n: '3', title: 'Confirm and pay', body: 'You confirm the quote and arrange payment by EFT.' },
  { n: '4', title: 'We procure', body: 'Procurement begins immediately after payment confirmation.' },
  { n: '5', title: 'Delivery and POD', body: 'Delivery and Proof of Delivery are finalised at your site.' },
] as const;

export function Home() {
  usePageMeta({
    title: 'Cleaning Supplies for Schools & Institutions',
    description:
      'Mopani Suppliers is a Phalaborwa-based B2B supplier of professional cleaning and hygiene products to schools, clinics and government offices across the Mopani District. Request a quote today.',
    path: '/',
  });

  return (
    <>
      <Hero
        title="Cleaning Supplies for Schools & Institutions"
        subtitle="Reliable Supply. Clean Spaces. Better Futures. Your trusted local procurement partner in the Mopani region."
        image="/assets/hero-home.jpg"
      >
        <Link href="/quote" className="btn btn-primary btn-hero">
          Request Quote
        </Link>
        <Link href="/catalogue" className="btn btn-ghost-navy btn-hero">
          View Catalogue
        </Link>
      </Hero>

      {/* Section 2 — trust strip. Baseline: white, 32px 0, single image. */}
      <section className="bg-white py-8">
        <div className="container-content flex justify-center">
          <img
            src="/assets/slogan-strip.png"
            alt="Quality Products · Reliable Service · Competitive Prices"
            width={720}
            height={64}
            loading="lazy"
            className="h-auto w-full max-w-3xl object-contain"
          />
        </div>
      </section>

      {/* Section 3 — muted. Now database-backed (A-4); production hardcoded 6 cards. */}
      <Section
        variant="muted"
        title="Our Product Range"
        intro="Professional-grade cleaning and hygiene supplies sourced for institutional needs."
      >
        <ProductGrid limit={6} />
        <div className="mt-12 flex justify-center">
          <Link href="/catalogue" className="btn btn-navy btn-hero">
            View Full Catalogue
          </Link>
        </div>
      </Section>

      {/* Section 4 — white. */}
      <Section
        variant="white"
        title="Why Choose Mopani Suppliers?"
        intro="We understand the procurement needs of schools and local government."
      >
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {WHY_CHOOSE.map((f) => (
            <div key={f.title}>
              <h3 className="font-heading text-base font-bold text-navy">{f.title}</h3>
              <p className="mt-3 text-charcoal/80">{f.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Section 5 — muted. */}
      <Section
        variant="muted"
        title="Our Procurement Process"
        intro="A compliant, paperwork-ready process designed for institutional buyers."
      >
        <ol className="grid gap-8 md:grid-cols-3 lg:grid-cols-5">
          {PROCESS.map((s) => (
            <li key={s.n}>
              <span
                className="flex h-10 w-10 items-center justify-center rounded-full bg-green-cta font-heading text-lg font-bold text-white"
                aria-hidden="true"
              >
                {s.n}
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-navy">{s.title}</h3>
              <p className="mt-2 text-sm text-charcoal/80">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      {/* Section 6 — navy CTA band. */}
      <CtaBand
        title="Ready to Stock Up?"
        body="Send us your requirements and we will prepare a formal quotation for your institution."
        action={
          <Link href="/quote" className="btn btn-primary btn-band">
            Request a Quote Today
          </Link>
        }
      />
    </>
  );
}
